import React, { useRef, useState } from 'react';
import { useData } from '../Data/DataContext';
import { startOfDay, subDays, addDays, isWithinInterval, formatDate, isSameDay } from 'date-fns';
import { Form as BootstrapForm, Button, Form, FormLabel, Modal, Row, Col, FormControl, Card, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import * as Yup from "yup";
import { Formik, FormikHelpers } from 'formik';
import axiosInstance from "../../utils/axiosConfig";
import CustomContainer from '../Utils/CustomContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faHeartPulse, faChild, faUserInjured, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import {IClientSickLeave} from "../../utils/FrontendDataTypes";

interface FormData {
  reason: string,
  fromDate: string,
  selectedClient?: string
}

const ReportNewSickLeave: React.FC = () => {
  // Daten aus dem DataContext verwenden
  const { data, refreshData } = useData();

  // Verwende die Daten direkt aus dem DataContext
  let actualClients = data?.clients || [];
  actualClients = actualClients.filter((c) => c.accompanied_by_employee === data?.employee_id)

  const actualEmployeeSickLeave = data?.employee_sick_leave || [];
  const actualClientSickLeave = data?.client_sick_leave || [];
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormData|null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formikActionsRef = useRef<FormikHelpers<FormData> | null>(null);

  const today = startOfDay(new Date());
  const twoDaysAgo = subDays(today, 2);
  const sevenDaysAhead = addDays(today, 7);

  const statusEmployeeIsSick = "employee_is_sick";
  const statusChildOfEmployeeIsSick = "child_of_employee_is_sick";
  const statusClientSick = "client_sick"

  const validationSchema = Yup.object({
    reason: Yup.string().required("Bitt einen Grund auswählen."),
    fromDate: Yup
        .date()
        .typeError('Ungültiges Datum')
        .required("Bitte wählen Sie ein Startdatum aus.")
        .test(
            'is-valid-range',
            `Datum muss zwischen dem ${formatDate(twoDaysAgo, 'dd.MM')} und dem ${formatDate(sevenDaysAhead, 'dd.MM')} liegen.`,
            (value) => {
              if (!value) {
                return true
              };
              return isWithinInterval(value, { start: twoDaysAgo, end: sevenDaysAhead });
            })
        .test(
            'existing_employee_sickleave',
            'Eine Krankmeldung für diesen Tag existiert bereits.',
            function(fromDate) {
              if (!fromDate) {
                return false
              }
              const {reason} = this.parent;
              if (reason === statusEmployeeIsSick || reason === statusChildOfEmployeeIsSick) {
                const fetchedExistingSickLeave = actualEmployeeSickLeave
                    .filter((es) => {
                      if (reason === statusEmployeeIsSick) {
                        return es.reason === 1;
                      } else {
                        return es.reason === 2;
                      }
                    })
                    .find((es) => {
                      const formFromDate = new Date(fromDate);
                      const from = new Date(es.from)
                      const to = (es.to ? new Date(es.to) : null);
                      if (to) {
                        return isWithinInterval(formFromDate, {start: from, end: to})
                      }
                      return isSameDay(formFromDate, from)
                    })
                return !fetchedExistingSickLeave
              }
              return true
            })
        .test(
            'existing_client_sick_leave',
            'Klient ist für diesen Tag schon Krank gemeldet.',
            function(fromDate) {
              if (!fromDate) {
                return false
              }
              const {reason, selectedClient} = this.parent;
              if (reason === statusClientSick) {
                const existingSickLeaves = (clientSickLeave: IClientSickLeave[]) => {
                  const fetched =  clientSickLeave.find((es) => {
                    const formFromDate = new Date(fromDate);
                    const from = new Date(es.from)
                    const to = (es.to ? new Date(es.to) : null);
                    if (to) {
                      return isWithinInterval(formFromDate, {start: from, end: to})
                    }
                    return isSameDay(formFromDate, from)
                  })
                  return fetched;
                }
                if (selectedClient) {
                  return !existingSickLeaves(actualClientSickLeave.filter((s) => s.client === Number(selectedClient)))
                }
                return !existingSickLeaves(actualClientSickLeave)
              }
              return true
            }),
    selectedClient: Yup
        .string()
        .when("reason", (reason, schema) => {
          if (reason.includes(statusClientSick)) {
            return schema.required('Wähle bitte einen Klienten aus')
          }
          return schema.notRequired()
        }),
  });

  const confirmSubmit = () => {
    setIsSubmitting(true);

    const reqFormData = new FormData();
    reqFormData.append("from", formData!.fromDate);
    reqFormData.append("sick_leave_type", formData!.reason);
    if (formData?.selectedClient) {
      reqFormData.append('clientId', String(formData?.selectedClient))
    }
    reqFormData.append('eau', 'false')

    axiosInstance
        .post("/create-or-update-sick-leave", reqFormData)
        .then(() => {
          if (formikActionsRef.current) {
            formikActionsRef.current.resetForm();
          }
          setShowModal(false);
          setSuccessMessage("Krankmeldung erfolgreich gesendet!");

          // Daten vom Backend nach erfolgreicher Krankmeldung neu laden
          refreshData()
            .then(() => {
              console.log('Daten nach Krankmeldung erfolgreich aktualisiert');
              // Nach erfolgreicher Aktualisierung zum Dashboard weiterleiten
              navigate('/dashboard');
            })
            .catch((error) => {
              console.error('Fehler beim Aktualisieren der Daten nach Krankmeldung:', error);
              // Auch bei Fehler zurück zum Dashboard navigieren
              navigate('/dashboard');
            })
            .finally(() => {
              setIsSubmitting(false);
            });

          // Erfolgsmeldung nach 3 Sekunden ausblenden
          setTimeout(() => setSuccessMessage(""), 3000);
        })
        .catch(() => {
          setShowModal(false);
          setErrorMessage("Fehler beim Senden der Krankmeldung. Bitte versuche es später erneut.");
          setIsSubmitting(false);
        });

  }

  const initialValues: FormData = {
    reason: '',
    fromDate: '',
    selectedClient: ''
  };

  return (
      <CustomContainer>
        <Card className="mb-4">
          <Card.Body>
            <h2 className="mb-3"><FontAwesomeIcon icon={faHeartPulse} className="me-2 text-danger" />Krankmelden</h2>

            {successMessage && (
              <Alert variant="success" dismissible onClose={() => setSuccessMessage("")}>                
                {successMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>                
                {errorMessage}
              </Alert>
            )}

            <Alert variant="info">
              <FontAwesomeIcon icon={faCircleInfo} className="me-2" />
              Füllen Sie bitte das Formular aus, um eine Krankmeldung einzureichen. Sie können die Krankmeldung später jederzeit bearbeiten.
            </Alert>
            <Row className="mt-4">
              <Formik
              validationSchema={validationSchema}
              initialValues={initialValues}
              validateOnBlur={true}
              validateOnChange={true}
              onSubmit={(values: FormData, actions: FormikHelpers<FormData>) => {
                formikActionsRef.current = actions;
                setFormData(values);
                setShowModal(true);
              }}
          >
            {({ setFieldValue, handleSubmit, values, errors, handleBlur, touched, handleChange }) => (
                <Form onSubmit={handleSubmit}>
                  <Card className="mb-4">
                    <Card.Body>
                      <h5 className="card-title mb-3">Krankmeldungsdaten</h5>
                      <Row>
                        {/** SICK LEAVE REASON */}
                        <Col md={6}>
                          {(() => {
                            const options = [
                              <option key={"please_choose_reason_of_sick_leave"} value="">Bitte wählen...</option>,
                              <option key={statusChildOfEmployeeIsSick} value={statusChildOfEmployeeIsSick}>Mein Kind ist krank</option>,
                              <option key={statusEmployeeIsSick} value={statusEmployeeIsSick}>Ich bin krank</option>,
                            ];

                            if (actualClients.length > 0) {
                              options.push(<option key={statusClientSick} value={statusClientSick}>Mein Klient ist krank</option>)
                            }

                            return (
                                <BootstrapForm.Group className='mb-3'>
                                  <FormLabel>Grund der Krankmeldung</FormLabel>
                                  <FormControl
                                      as="select"
                                      name="reason"
                                      className="form-control"
                                      onBlur={handleBlur}
                                      isInvalid={touched.reason && Boolean(errors.reason)}
                                      onChange={(e) => {
                                        setFieldValue("reason", e.target.value);
                                        setFieldValue('selectedClient', null)
                                      }}
                                  >
                                    {options}
                                  </FormControl>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.reason}
                                  </Form.Control.Feedback>
                                </BootstrapForm.Group>
                            )
                          })()}
                        </Col>

                        {/** FROM DATE */}
                        <Col md={6}>
                          <BootstrapForm.Group className='mb-3'>
                            <FormLabel>Krankmeldung ab</FormLabel>
                            <FormControl
                                type="date"
                                name="fromDate"
                                className="form-control"
                                isInvalid={touched.fromDate && Boolean(errors.fromDate)}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                min={formatDate(twoDaysAgo, 'yyyy-MM-dd')}
                                max={formatDate(sevenDaysAhead, 'yyyy-MM-dd')}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.fromDate}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              Erlaubter Zeitraum: {formatDate(twoDaysAgo, 'dd.MM.yyyy')} bis {formatDate(sevenDaysAhead, 'dd.MM.yyyy')}
                            </Form.Text>
                          </BootstrapForm.Group>
                        </Col>
                      </Row>

                      {values.reason === statusClientSick && (
                        <Row className="mt-0">
                          <Col>
                            <BootstrapForm.Group className="mb-0">
                              <FormLabel>Betroffenen Klienten auswählen:</FormLabel>
                              <FormControl
                                as="select"
                                name="selectedClient"
                                className="form-control"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                isInvalid={touched.selectedClient && Boolean(errors.selectedClient)}
                              >
                                <option key="please_choose" value="">Bitte wählen...</option>
                                {actualClients.map((client) => (
                                  <option key={client.record_id} value={client.record_id}>
                                    {client.first_name} {client.last_name}
                                  </option>
                                ))}
                              </FormControl>
                              <Form.Control.Feedback type="invalid">
                                {errors.selectedClient}
                              </Form.Control.Feedback>
                            </BootstrapForm.Group>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>

                  <div className="d-flex justify-content-between mt-3">
                    <Button variant="outline-secondary" onClick={() => navigate('/dashboard')} disabled={isSubmitting}>
                      Zurück zum Dashboard
                    </Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Wird gespeichert...
                        </>
                      ) : (
                        'Krankmeldung speichern'
                      )}
                    </Button>
                  </div>

                </Form>
            )}
          </Formik>
            </Row>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faHeartPulse} className="me-2 text-danger" />
              Krankmeldung bestätigen
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Möchten Sie die folgende Krankmeldung wirklich speichern?</p>

            {formData && (
              <Card className="bg-light">
                <Card.Body>
                  <Row className="mb-2">
                    <Col xs={4} className="fw-bold">Grund:</Col>
                    <Col xs={8}>
                      {formData.reason === statusEmployeeIsSick && "Eigene Krankmeldung"}
                      {formData.reason === statusChildOfEmployeeIsSick && "Kind ist krank"}
                      {formData.reason === statusClientSick && "Klient ist krank"}
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={4} className="fw-bold">Datum:</Col>
                    <Col xs={8}>{new Date(formData.fromDate).toLocaleDateString('de-DE')}</Col>
                  </Row>
                  {formData.selectedClient && (
                    <Row>
                      <Col xs={4} className="fw-bold">Klient:</Col>
                      <Col xs={8}>
                                                 {actualClients.find(c => c.record_id.toString() === formData.selectedClient)?.first_name + ' ' + 
                         actualClients.find(c => c.record_id.toString() === formData.selectedClient)?.last_name}
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSubmitting}>Abbrechen</Button>
            <Button variant="primary" onClick={confirmSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Wird gesendet...
                </>
              ) : (
                'Bestätigen'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </CustomContainer>
  )
};

export default ReportNewSickLeave;